import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { NavFoldButton } from '@/components/NavFoldGlyph';
import { useT } from '@/i18n/LocaleProvider';
import {
  readSidebarGroupExpanded,
  writeSidebarGroupExpanded,
} from '@/components/sidebarGroupExpansion';

export interface HomeNavigationGroupProps {
  /** Whose fold state this is: remembered per work area and per device, as the Views group's is. */
  workAreaId: string;
  /** Where Home goes. */
  homeTo: string;
  /** Whether Home itself is the place being read. */
  homeActive: boolean;
  /** Whether the page being read is one of Home's pages, which a folded Home then marks. */
  pageOpen: boolean;
  /** Whether Home has pages under it; with none there is nothing to fold, and no fold is drawn. */
  hasPages: boolean;
  /** Home's own icon, which the fold takes the place of on hover, as every fold in the sidebar. */
  icon: ReactNode;
  linkClassName: (state: { isActive: boolean }) => string;
  /** The sidebar line's icon size (`Layout.tsx`'s `lineIcon`), which Home is one of. */
  lineIconClassName: string;
  onNavigate?: () => void;
  /** Home's own controls, after its title ("New page"). */
  actions?: ReactNode;
  /** The page tree under Home (`PageNavigationList`), which indents its own rows. */
  children?: ReactNode;
}

/**
 * Home and the work area's pages under it (I-1817, D-0305 c). Jay, 2026-10-14: "it should be the
 * same folder structure as before the change, just collapsible. I.e. first home then on next levels
 * custom pages". So there is no "Pages" header (I-1389's, and I-1594's option a): Home is the
 * first-level line, the pages are its children one level in, and they fold from Home itself.
 *
 * Home has a page of its own, so by I-1332's rule its title opens it and its icon's place folds
 * the pages, a real button (`NavFoldButton`), as the Views header does. The button says
 * `aria-expanded`, names what it folds, and takes → to open and ← to close. Home itself never folds
 * away. "New page" sits beside Home (`actions`), where it was before I-1389.
 *
 * The fold is remembered per work area and per device under the key the Pages header used, so a
 * reader who had folded their pages finds them folded still.
 */
export function HomeNavigationGroup({
  workAreaId,
  homeTo,
  homeActive,
  pageOpen,
  hasPages,
  icon,
  linkClassName,
  lineIconClassName,
  onNavigate,
  actions,
  children,
}: HomeNavigationGroupProps) {
  const groupId = `sidebar-home-pages-${workAreaId}`;
  // Read once, and again for each work area: each keeps this reader's own choice.
  const t = useT();
  const [expanded, setExpanded] = useState(() => readSidebarGroupExpanded('pages', workAreaId));
  useEffect(() => {
    setExpanded(readSidebarGroupExpanded('pages', workAreaId));
  }, [workAreaId]);

  function remember(next: boolean): void {
    setExpanded(next);
    writeSidebarGroupExpanded('pages', workAreaId, next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (!hasPages || event.altKey || event.metaKey || event.ctrlKey) return;
    if (!(event.target instanceof HTMLElement) || !event.target.closest('[data-nav-fold-toggle]')) {
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (!expanded) remember(true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (expanded) remember(false);
    }
  }

  const open = hasPages && expanded;
  return (
    // Home and its pages a line's step apart, as every other pair of lines (I-1328).
    <div className="space-y-0.5" data-home-group="">
      <div className="flex items-center gap-0.5">
        <div
          data-home-line=""
          onKeyDown={onKeyDown}
          // Folded, Home is the only line left to say the reader is on one of its pages. A line
          // under a coarse pointer is a 44 px target (I-1308).
          className={`${linkClassName({ isActive: homeActive || (!open && pageOpen) })} nav-fold min-w-0 flex-1 [@media(pointer:coarse)]:min-h-touch`}
        >
          {hasPages ? (
            <NavFoldButton
              icon={icon}
              expanded={open}
              // What the fold names, for its label and title: "Collapse pages under Home".
              name={t('navigation.sidebar.pagesUnderHome')}
              sizeClassName={lineIconClassName}
              controls={groupId}
              onToggle={() => remember(!expanded)}
            />
          ) : (
            <span className="grid shrink-0 place-items-center">{icon}</span>
          )}
          <NavLink
            to={homeTo}
            end
            onClick={onNavigate}
            data-home-link=""
            className="nav-line-link flex min-w-0 flex-1 items-center self-stretch rounded outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <span className="min-w-0 flex-1 truncate text-left">{t('navigation.home')}</span>
          </NavLink>
        </div>
        {actions}
      </div>
      {open ? (
        <div
          id={groupId}
          role="group"
          aria-label={t('navigation.pagesUnderHome')}
          className="space-y-0.5"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
